import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { monitorModaldata } from "../layout/types";
import AxiosInstance from "../utils/axios";
import { notification } from "antd";
import { WorkorderDownloadAPICall } from "../utils/platFormAxios";
import { useAppState } from "../../lib/appContext/AppContext";
import { useAuth } from "../../lib/auth/AuthContext";
import { downloadWorkOrder } from "../../pages/new-trail/utils";
import { formatTranslation } from "../utils/translationUtils";
import { ExportEvents } from "../utils/amplitudeEvents";
import { MonitorBrands } from "../layout/Modal/Monitor.types";
import { useTrackingEvent } from "./useTracking";

const useDownloadWorkOrder = (
  exportSource: "button" | "report" = "button"
): any => {
  const { stateValue } = useAppState();
  const { user } = useAuth();
  const { t } = useTranslation();
  const trackExportEvents = useTrackingEvent({
    group: "Export",
    module: "Export",
  });
  const [isLoading, setIsLoading] = useState(false);
  const orgName = stateValue?.orgValue?.name ?? "";
  const farmName = stateValue?.farmValue?.name ?? "";
  const key = "updatable";

  const openNotification = (msg: boolean, error?: string): void => {
    notification.open({
      key,
      type: msg ? "info" : "error",
      message: msg
        ? formatTranslation(t("workOrder.downloadInProgress"))
        : formatTranslation(t("workOrder.downloadFailed")),
      description: error,
      duration: msg ? 50 : 2.5,
    });
  };

  const downloadmonitorModalForm = async (
    data: monitorModaldata,
    trialId?: string,
    fieldId?: string
  ): Promise<void> => {
    setIsLoading(true);
    openNotification(true);
    const baseDownloadApiUrl =
      process.env.REACT_APP_BASE_API_DOWNLOAD_MONITOR_ENDPOINT ?? "";
    try {
      const downloadApiResponse = await WorkorderDownloadAPICall.post(
        `${baseDownloadApiUrl}/shapefile?format=${
          data.selectedmonitorBrand ?? ""
        }&monitor=${data.selectedmonitorModel ?? ""}`,
        {
          geojson: data.geoJSONData,
        }
      );
      const blob = await downloadApiResponse?.data;
      downloadWorkOrder(blob);
      void trackExportEvents({
        event: ExportEvents.TRIAL_DATA_EXPORTED,
        export_source: exportSource,
        export_target:
          MonitorBrands.find(
            (brand) => brand.format === data.selectedmonitorBrand
          )?.label ?? data.selectedmonitorBrand,
        trial_id: trialId,
        field_id: fieldId,
      });
      notification.open({
        key,
        type: "success",
        message: formatTranslation(t("workOrder.downloadSuccess")),
      });
      setIsLoading(false);
    } catch (error) {
      openNotification(false, (error as Error).message);
      setIsLoading(false);
      console.error("Error While Fetching Data", error);
    }
  };

  const downloadISOXMLOrder = async (
    fielFormdData: any,
    trailId: string
  ): Promise<void> => {
    setIsLoading(true);
    openNotification(true);
    try {
      const downloadApiResponse = await AxiosInstance.post(
        `work_order/isoxml?trial_id=${trailId}`,
        {
          field_name: fielFormdData.name,
          organization_name: orgName,
          property_name: farmName,
          user_id: user?.id,
          field_geometry: fielFormdData.geometry,
        }
      );
      const downloadURL = downloadApiResponse?.data;
      window.location.assign(downloadURL.download_url);
      void trackExportEvents({
        event: ExportEvents.TRIAL_DATA_EXPORTED,
        export_source: exportSource,
        export_target: "ISO-XML",
        trial_id: trailId,
        field_id: fielFormdData.id,
      });
      notification.open({
        key,
        type: "success",
        message: formatTranslation(t("workOrder.downloadSuccess")),
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error While Fetching Data", error);
      openNotification(false, (error as Error).message);
      setIsLoading(false);
    }
  };

  return { downloadmonitorModalForm, downloadISOXMLOrder, isLoading };
};

export default useDownloadWorkOrder;
